import React, { useState } from 'react';
import api from '../services/api';

// Same list as the app (frontEnd/eduthrift/src/utils/banks.ts): universal branch codes per bank.
const SA_BANKS = [
  { name: 'ABSA', branchCode: '632005' },
  { name: 'Access Bank', branchCode: '410105' },
  { name: 'African Bank', branchCode: '430000' },
  { name: 'Bank Zero', branchCode: '888000' },
  { name: 'Bidvest Bank', branchCode: '462005' },
  { name: 'Capitec Bank', branchCode: '470010' },
  { name: 'Capitec Business', branchCode: '450105' },
  { name: 'Discovery Bank', branchCode: '679000' },
  { name: 'FNB', branchCode: '250655' },
  { name: 'Grindrod Bank', branchCode: '584000' },
  { name: 'Investec', branchCode: '580105' },
  { name: 'Nedbank', branchCode: '198765' },
  { name: 'Postbank', branchCode: '460005' },
  { name: 'RMB Private Bank', branchCode: '250655' },
  { name: 'Sasfin Bank', branchCode: '683000' },
  { name: 'Standard Bank', branchCode: '051001' },
  { name: 'TymeBank', branchCode: '678910' },
];

const ACCOUNT_TYPES = [
  { value: 'cheque', label: 'Cheque / Current' },
  { value: 'savings', label: 'Savings' },
  { value: 'transmission', label: 'Transmission' },
];

/**
 * Changes a payout bank account after support has checked the bank's confirmation letter.
 * For a seller pass `user`; for a school shop pass `shop` (also asks for the account holder).
 * `current` holds the account on file (snake_case, as the admin API returns it).
 */
const BankDetailsEditModal = ({ user, shop, current = {}, onClose, onSaved }) => {
  const isShop = !!shop;
  const [form, setForm] = useState({
    bankAccountHolder: current.bank_account_holder || '',
    bankName: current.bank_name || '',
    bankAccountType: current.bank_account_type || '',
    bankAccountNumber: '',
    bankBranchCode: current.bank_branch_code || '',
    confirmed: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const chooseBank = (name) => {
    const bank = SA_BANKS.find(b => b.name === name);
    setForm(f => ({ ...f, bankName: name, bankBranchCode: bank ? bank.branchCode : f.bankBranchCode }));
  };

  const owner = isShop ? shop.name : `${user.first_name} ${user.last_name}`;
  const currentAccount = current.bank_account_number
    ? `${current.bank_name || ''} •••• ${String(current.bank_account_number).slice(-4)}`
    : 'none on file';

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const url = isShop ? `/admin/school-shops/${shop.id}/bank-details` : `/admin/users/${user.id}/bank-details`;
      const res = await api.put(url, form);
      onSaved(res.data?.message || 'Bank details updated');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update bank details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={dialog}>
        <h2 style={{ margin: '0 0 4px' }}>Change bank details</h2>
        <p style={{ margin: '0 0 16px', color: '#666', fontSize: '14px' }}>
          {owner} — current account: <strong>{currentAccount}</strong>
        </p>

        {isShop && (
          <label style={labelStyle}>
            Account holder (the school's name at the bank)
            <input value={form.bankAccountHolder} onChange={e => set('bankAccountHolder', e.target.value)} style={input} />
          </label>
        )}
        <label style={labelStyle}>
          Bank
          <select value={form.bankName} onChange={e => chooseBank(e.target.value)} style={input}>
            <option value="">Select bank</option>
            {SA_BANKS.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          Account type
          <select value={form.bankAccountType} onChange={e => set('bankAccountType', e.target.value)} style={input}>
            <option value="">Select type</option>
            {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          New account number
          <input value={form.bankAccountNumber} onChange={e => set('bankAccountNumber', e.target.value)}
                 inputMode="numeric" autoComplete="off" style={input} />
        </label>
        <label style={labelStyle}>
          Branch code
          <input value={form.bankBranchCode} onChange={e => set('bankBranchCode', e.target.value)} inputMode="numeric" style={input} />
        </label>

        <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '14px', margin: '8px 0 12px' }}>
          <input type="checkbox" checked={form.confirmed} onChange={e => set('confirmed', e.target.checked)} style={{ width: 'auto', marginTop: '3px' }} />
          <span>
            I have checked the bank's confirmation letter (stamped or digitally verified, not older than 3 months)
            {isShop ? " and the account is in the school's name." : ' and the account is in the seller’s name.'}
          </span>
        </label>

        <p style={{ fontSize: '13px', color: '#856404', backgroundColor: '#fff3cd', padding: '8px 10px', borderRadius: '4px', margin: '0 0 12px' }}>
          New sales pay into the new account. Orders already in progress still pay out to the previous account.
          {isShop ? ' The shop owner' : ' The seller'} is emailed about the change.
        </p>

        {error && <p style={{ color: '#c0392b', margin: '0 0 12px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={saving} style={{ ...button, backgroundColor: '#95a5a6' }}>Cancel</button>
          <button onClick={save} disabled={saving || !form.confirmed} style={{ ...button, backgroundColor: form.confirmed ? '#27ae60' : '#bdc3c7' }}>
            {saving ? 'Saving...' : 'Save new account'}
          </button>
        </div>
      </div>
    </div>
  );
};

const overlay = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
};
const dialog = { backgroundColor: 'white', borderRadius: '10px', padding: '24px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflow: 'auto' };
const labelStyle = { display: 'block', fontSize: '13px', color: '#555', marginBottom: '10px' };
const input = { display: 'block', width: '100%', padding: '8px', marginTop: '4px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' };
const button = { width: 'auto', padding: '8px 16px', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' };

export default BankDetailsEditModal;
